/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    classNamesFunction,
    DefaultButton,
    FontWeights,
    IStackStyles,
    IStackTokens,
    Link,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    ProgressIndicator,
    Stack,
    TooltipHost
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { IImageStyleProps, IImageStyles, Image } from '@fluentui/react/lib/Image';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { BasicModal } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { routeTable } from 'src/core/utils/routeTable';
import { SavingIndicator } from 'src/customText/appShell/dataTaggingPage/components/SavingIndicator';
import { SavingState } from 'src/customText/appshell/dataTaggingPage/hooks/useSaveChanges';
import { AmlWarningModal } from 'src/customText/core/components/AmlWarningModal';
import { constructAmlProjectUrl } from 'src/customText/core/utils/constructAmlProjectUrl';
import { AzureMLConnectionModal } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';

export enum SwitchingTypes {
    Stable = 0,
    Freezing = 1,
    Exporting = 2
}

type AmlTabProps = {
    savingState: SavingState;
};

const messages = defineMessages({
    description: {
        id: 'customText.customTextAzureMLTab.description',
        defaultMessage:
            'Now you can do data labeling for your project in Azure Machine Learning: discover easier collaboration, more flexibility, and even vendor labeling options.'
    },
    connectToAml: {
        id: 'customText.customTextAzureMLTab.connectToAml',
        defaultMessage: 'Connect to Azure Machine Learning'
    },
    labelEnabledMessageBar: {
        id: 'customText.customTextAzureMLTab.labelEnabledMessageBar',
        defaultMessage: 'This project is enabled in Language Studio. You can switch to or disconnect from Azure Machine Learning.'
    },
    labelDisabledMessageBar: {
        id: 'customText.customTextAzureMLTab.labelDisabledMessageBar',
        defaultMessage: 'Labeling activity is disabled in Language Studio'
    },
    freezingStateBold: {
        id: 'customText.customTextAzureMLTab.freezingStateBold',
        defaultMessage: 'Switching labeling activity step 1 of 2:'
    },
    freezingState: {
        id: 'customText.customTextAzureMLTab.freezingState',
        defaultMessage: 'Freezing the state of your project and disabling any labeling activities ... this may take a few minutes'
    },
    exportingStateBold: {
        id: 'customText.customTextAzureMLTab.exportingStateBold',
        defaultMessage: 'Switching labeling activity step 2 of 2:'
    },
    exportingState: {
        id: 'customText.customTextAzureMLTab.exportingState',
        defaultMessage: 'Exporting your labels ... this may take a few minutes'
    },
    completeState: {
        id: 'customText.customTextAzureMLTab.completeState',
        defaultMessage: `Switching complete ...
         your project is now ready for labeling in Azure Machine Learning`
    },
    continueLabelingAml: {
        id: 'customText.customTextAzureMLTab.continueLabelingAml',
        defaultMessage: 'Continue labeling in Azure Machine Learning'
    },
    switchLabelingActivity: {
        id: 'customText.customTextAzureMLTab.switchLabelingActivity',
        defaultMessage: 'Switch labeling activity'
    },
    switchDescription: {
        id: 'customText.customTextAzureMLTab.switchDescription',
        defaultMessage: 'If you prefer to label in Azure Machine Learning, switch the labeling activity.'
    },
    labelingDisabledDescription: {
        id: 'customText.customTextAzureMLTab.labelingDisabledDescription',
        defaultMessage: 'Go to Azure Machine Learning to continue labeling, or to switch the labeling activity back to Language Studio.'
    },
    gotoAml: {
        id: 'customText.customTextAzureMLTab.gotoAml',
        defaultMessage: 'Go to Azure Machine Learning'
    },
    switchText: {
        id: 'customText.customTextAzureMLTab.switchText',
        defaultMessage: 'Switch to Azure Machine Learning'
    },
    modalContent: {
        id: 'customText.customTextAzureMLTab.modalContent',
        defaultMessage:
            'Labeling activity is enabled here. If you prefer to label in Azure Machine Learning, switch the labeling activity location. Once you switch, labeling activity will be disabled here in Language Studio. You can switch back from Azure Machine Learning Studio to Language Studio at anytime.'
    },
    switch: {
        id: 'customText.customTextAzureMLTab.switch',
        defaultMessage: 'Switch'
    },
    cancel: {
        id: 'customText.customTextAzureMLTab.cancel',
        defaultMessage: 'Cancel'
    },
    disconnect: {
        id: 'customText.customTextAzureMLTab.disconnect',
        defaultMessage: 'Disconnect from Azure Machine Learning'
    },
    doNotShowModalAgain: {
        id: 'customText.customTextAzureMLTab.doNotShowModalAgain',
        defaultMessage: 'Do not show this message again.'
    },
    classificationInstructionModalTitle: {
        id: 'customText.customTextAzureMLTab.classificationInstructionModalTitle',
        defaultMessage: 'There must be at least two classes created in your project'
    },
    nerInstructionModalTitle: {
        id: 'customText.customTextAzureMLTab.nerInstructionModalTitle',
        defaultMessage: 'There must be at least one entity created in your project'
    },
    classificationInstructionModalContent: {
        id: 'customText.customTextAzureMLTab.classificationInstructionModalContent',
        defaultMessage: 'Before you to connect to with Azure Machine Learning, you need to have at least two classes created.'
    },
    nerInstructionModalContent: {
        id: 'customText.customTextAzureMLTab.nerInstructionModalContent',
        defaultMessage: 'Before you to connect to with Azure Machine Learning, you need to have at least one entity created.'
    },
    gotIt: {
        id: 'customText.customTextAzureMLTab.gotIt',
        defaultMessage: 'Got it'
    },
    connectedDesc: {
        id: 'customText.customTextAzureMLTab.connectedDesc',
        defaultMessage: 'Manage labeling activity between Language Studio and Azure Machine Learning here.'
    },
    connectedDescLink: {
        id: 'customText.customTextAzureMLTab.connectedDescLink',
        defaultMessage: 'learn more about labeling activity'
    },
    settings: {
        id: 'customText.customTextAzureMLTab.settings',
        defaultMessage: 'Settings'
    },
    error: {
        id: 'customText.customTextAzureMLTab.error',
        defaultMessage: 'An Error happened'
    },
    switching: {
        id: 'customText.customTextAzureMLTab.switching',
        defaultMessage: 'Your labels are switching to Azure Machine Learning'
    },
    multilingual: {
        id: 'customText.customTextAzureMLTab.multilingual',
        defaultMessage: 'You can not connect to Azure Machine Learning if your project is multi-lingual.'
    }
});

const stackTokens: IStackTokens = { padding: 10, childrenGap: 40 };
const cardStyle: IStackStyles = {
    root: {
        boxShadow: ' 0 2px 4px 0 rgba(0, 0, 0, .13), 0 1px 1px 0 rgba(0, 0, 0, .11)',
        padding: '9px'
    }
};

const imageStyles = classNamesFunction<IImageStyleProps, IImageStyles>()({
    image: { width: '100%', height: 'auto' }
});

export const CustomTextAzureMLTab = observer((props: AmlTabProps) => {
    const { savingState } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const [isSwitchModalOpen, { setTrue: showSwitchModal, setFalse: hideSwitchModal }] = useBoolean(false);
    const [isLabelInstructionModalOpen, { setTrue: showLabelInstructionModal, setFalse: hideLabelInstructionModal }] = useBoolean(false);
    const [amlProjectUrl, setAmlProjectUrl] = React.useState('');

    const currentProject = rootStore.currentCustomTextProjectStore;
    const {
        name: currProjectName,
        projectKind: currentModelType,
        isLabelingLocked,
        amlProjectPath,
        switchToAmlState,
        multiLingual
    } = currentProject;
    const isExtractionProject = currentModelType === 'CustomEntityRecognition';
    const history = useHistory();
    const switchingTracker = useProgressTracker({ persistOnComplete: true });

    const routeToSetting = () =>
        history.push(routeTable.customText.projectSettingsPage(currProjectName, currentModelType).concat('#amlConnectionButtonId'));
    const [isAzureMLConnectionModalHidden, { setTrue: hideIsAzureMLConnectionModal, setFalse: showAzureMLConnectionModal }] = useBoolean(
        true
    );

    const handleSwitchButton = () => {
        currentProject.setSwitchToAmlState(SwitchingTypes.Freezing);
        logButtonClick(isExtractionProject ? 'ner' : 'classification', 'AMLPivot', 'AML:SwitchLabels', 'AzureMachineLearning');
        dispatcher.dispatch('switchLabelToAml', {
            tracker: switchingTracker,
            toastData: {
                failed: { title: localize(messages.error) },
                completed: { title: localize(messages.completeState) },
                running: { title: localize(messages.switching) }
            }
        });
        hideSwitchModal();
    };

    React.useEffect(() => {
        const url = constructAmlProjectUrl(amlProjectPath);
        setAmlProjectUrl(url);
    }, [amlProjectPath]);

    const handleOpenAzureMLConnectionModal = () => {
        if (currentProject?.extractionStore?.entities?.length < 1) {
            showLabelInstructionModal();
        } else if (currentProject?.classificationStore?.classes?.length < 2) {
            showLabelInstructionModal();
        } else {
            logButtonClick(
                isExtractionProject ? 'ner' : 'classification',
                'AMLPivot',
                'AML:ConnectToAzureMachineLearning',
                'AzureMachineLearning'
            );
            showAzureMLConnectionModal();
        }
    };

    const getSwitchingState = () => {
        switch (switchToAmlState) {
            case SwitchingTypes.Freezing:
                return (
                    <>
                        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { minHeight: 'auto' } }}>
                            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>
                                {localize(messages.freezingStateBold)}
                            </LuisText>
                            &nbsp;
                            {localize(messages.freezingState)}
                        </MessageBar>
                        <ProgressIndicator />
                    </>
                );

            case SwitchingTypes.Exporting:
                return (
                    <>
                        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { minHeight: 'auto' } }}>
                            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>
                                {localize(messages.exportingStateBold)}
                            </LuisText>
                            &nbsp;
                            {localize(messages.exportingState)}
                        </MessageBar>
                        <ProgressIndicator />
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {switchToAmlState ? (
                getSwitchingState()
            ) : amlProjectPath ? (
                isLabelingLocked ? (
                    <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { minHeight: 'auto' } }}>
                        {localize(messages.labelDisabledMessageBar)}
                    </MessageBar>
                ) : (
                    <MessageBar styles={{ root: { minHeight: 'auto' } }}>{localize(messages.labelEnabledMessageBar)}</MessageBar>
                )
            ) : null}
            <Stack verticalFill tokens={stackTokens}>
                {amlProjectPath ? (
                    isLabelingLocked ? (
                        <Stack verticalFill verticalAlign="space-between" tokens={stackTokens}>
                            <Stack tokens={{ childrenGap: 10 }}>
                                <LuisText>
                                    {localize(messages.connectedDesc)} &nbsp;
                                    <ExternalLink
                                        href={
                                            isExtractionProject
                                                ? 'https://aka.ms/ner-StudioAMLIntegration'
                                                : 'https://aka.ms/classification-StudioAMLIntegration'
                                        }>
                                        {localize(messages.connectedDescLink)}
                                    </ExternalLink>
                                </LuisText>
                                <Stack styles={cardStyle}>
                                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                                        <Stack.Item disableShrink grow={3}>
                                            <Image
                                                styles={imageStyles}
                                                src={getAssetsUrl('assets/images/customText/amlActivityPane.svg')}
                                            />
                                        </Stack.Item>
                                        <Stack.Item grow={1}>
                                            <Stack tokens={{ childrenGap: 10 }}>
                                                <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>
                                                    {localize(messages.continueLabelingAml)}
                                                </LuisText>
                                                <LuisText>{localize(messages.labelingDisabledDescription)}</LuisText>

                                                <ExternalLink disabled={switchToAmlState !== 0} href={amlProjectUrl}>
                                                    {localize(messages.gotoAml)}
                                                </ExternalLink>
                                            </Stack>
                                        </Stack.Item>
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Stack.Item align="center">
                                <ExternalLink onClick={routeToSetting}>{localize(messages.disconnect)}</ExternalLink>
                            </Stack.Item>
                        </Stack>
                    ) : (
                        <Stack verticalFill verticalAlign="space-between" tokens={stackTokens}>
                            <Stack tokens={{ childrenGap: 10 }}>
                                <LuisText>
                                    {localize(messages.connectedDesc)} &nbsp;
                                    <ExternalLink onClick={routeToSetting}>{localize(messages.settings)}</ExternalLink>
                                </LuisText>
                                <Stack styles={cardStyle} horizontal tokens={{ childrenGap: 10 }}>
                                    <Stack.Item disableShrink grow={3}>
                                        <Image styles={imageStyles} src={getAssetsUrl('assets/images/customText/amlActivityPane.svg')} />
                                    </Stack.Item>
                                    <Stack.Item grow={1}>
                                        <Stack tokens={{ childrenGap: 10 }}>
                                            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>
                                                {localize(messages.switchLabelingActivity)}
                                            </LuisText>
                                            <LuisText>{localize(messages.switchDescription)}</LuisText>
                                            {savingState !== 'saved' ? (
                                                <SavingIndicator savingState={savingState} />
                                            ) : (
                                                <ExternalLink onClick={showSwitchModal}>{localize(messages.switchText)}</ExternalLink>
                                            )}
                                        </Stack>
                                    </Stack.Item>
                                </Stack>
                            </Stack>
                            <Stack.Item align="center">
                                <ExternalLink onClick={routeToSetting}>{localize(messages.disconnect)}</ExternalLink>
                            </Stack.Item>
                        </Stack>
                    )
                ) : (
                    <>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <LuisText>{localize(messages.description)}</LuisText>
                            {savingState !== 'saved' ? (
                                <Stack horizontalAlign="center">
                                    <SavingIndicator savingState={savingState} onDiscard={() => {}} />
                                </Stack>
                            ) : (
                                <TooltipHost content={localize(messages.multilingual)} hidden={!multiLingual}>
                                    <Link disabled={multiLingual} onClick={handleOpenAzureMLConnectionModal}>
                                        {localize(messages.connectToAml)}
                                    </Link>
                                </TooltipHost>
                            )}
                        </Stack>
                        <AmlWarningModal
                            hidden={!isLabelInstructionModalOpen}
                            isExtractionProject={isExtractionProject}
                            closeModal={hideLabelInstructionModal}
                        />
                        <AzureMLConnectionModal hidden={isAzureMLConnectionModalHidden} onDismiss={hideIsAzureMLConnectionModal} />
                    </>
                )}

                <BasicModal
                    minWidth={416}
                    onDismiss={hideSwitchModal}
                    hidden={!isSwitchModalOpen}
                    modalProps={{ isModeless: true }}
                    shouldRenderFooter={false}
                    dialogContentProps={{ title: localize(messages.switchLabelingActivity) }}>
                    <Stack tokens={stackTokens}>
                        <Stack>
                            <LuisText>{localize(messages.modalContent)}</LuisText>
                        </Stack>
                        <Stack tokens={{ childrenGap: 10 }} horizontal horizontalAlign="end">
                            <PrimaryButton text={localize(messages.switch)} onClick={handleSwitchButton} />
                            <DefaultButton text={localize(messages.cancel)} onClick={hideSwitchModal} />
                        </Stack>
                    </Stack>
                </BasicModal>
            </Stack>
        </>
    );
});
